// Legal pages — Imprint, Privacy, Terms — multilingual (DE/FR/IT/EN).
// All content is rendered from one shared CONTENT dict per locale and surface.
// Company / contact data lives in /config/company.js (single source of truth).

import React, { useEffect } from "react";
import { useLang } from "../contexts/LanguageContext";
import { Link } from "react-router-dom";
import { buildPath } from "../lib/i18n";
import { ShieldCheck, FileText, Scale, Mail, MapPin, Phone, ArrowLeft, Clock, MessageCircle } from "lucide-react";
import { COMPANY, RESPONSE_TIME_COPY } from "../config/company";

// Backwards-compatible alias for any legacy importers. Prefer COMPANY directly.
export const CONTACT_INFO = {
  company: COMPANY.name,
  email: COMPANY.emails.general,
  privacyEmail: COMPANY.emails.privacy,
  legalEmail: COMPANY.emails.legal,
  supportEmail: COMPANY.emails.support,
  country: COMPANY.country,
};

// ---------------------------------------------------------------------------
// I18N CONTENT
// ---------------------------------------------------------------------------
const CONTENT = {
  de: {
    legalNav: { imprint: "Impressum", privacy: "Datenschutz", terms: "AGB", contact: "Kontakt" },
    back: "Zurück",
    updated: "Stand: Mai 2026",
    imprint: {
      title: "Impressum",
      subtitle: "Angaben gemäss Schweizer Recht.",
      operator: "Betreiber",
      contact: "Kontakt",
      register: "Eintragung",
      uid: "UID",
      disclaimerTitle: "Haftungsausschluss",
      disclaimer: "Die Inhalte dieser Webseite werden mit grösster Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der bereitgestellten Inhalte übernehmen wir jedoch keine Gewähr. Inserate werden von Drittnutzern eingestellt; CarHive ist nicht für deren Inhalt verantwortlich.",
      linksTitle: "Externe Links",
      links: "Trotz sorgfältiger inhaltlicher Kontrolle übernehmen wir keine Haftung für Inhalte externer Links. Für den Inhalt der verlinkten Seiten sind ausschliesslich deren Betreiber verantwortlich.",
      copyrightTitle: "Urheberrecht",
      copyright: "Alle Inhalte und Werke auf dieser Webseite unterliegen dem schweizerischen Urheberrecht. Vervielfältigung, Bearbeitung, Verbreitung jeder Art bedürfen der vorherigen schriftlichen Zustimmung.",
    },
    privacy: {
      title: "Datenschutzerklärung",
      subtitle: "DSGVO- und CH-DSG-konform.",
      sections: [
        { h: "1. Verantwortliche Stelle", b: "Verantwortlich für die Datenverarbeitung im Sinne des CH-DSG und der DSGVO ist:\n\nArnaldo Flávio Gonçalves Tafolli\nCarHive\nZelglistrasse 11\n8953 Dietikon\nSchweiz\n\nKontakt: datenschutz@carhive.ch." },
        { h: "2. Erfasste Daten", b: "Wir verarbeiten: Konto-Daten (E-Mail, Name, Passwort-Hash), Inserats- und Nachrichten-Inhalte, Standort-Angaben aus Inseraten, IP-Adresse (gekürzt), Cookies sowie technische Logs zur Sicherheit." },
        { h: "3. Zweck der Verarbeitung", b: "Bereitstellung des Marktplatzes, Verwaltung deines Kontos, Kommunikation zwischen Käufer und Verkäufer, Sicherheit gegen Missbrauch, gesetzliche Aufbewahrung, anonymisierte Analyse zur Verbesserung des Angebots." },
        { h: "4. Rechtsgrundlagen", b: "Vertrag (Art. 6 Abs. 1 lit. b DSGVO) für Konto und Inserate; berechtigtes Interesse (lit. f) für Sicherheit und Missbrauchsschutz; Einwilligung (lit. a) für optionale Push-Benachrichtigungen, Marketing-Mails und nicht-essentielle Cookies." },
        { h: "5. Zahlungsabwicklung — Stripe", b: "Für kostenpflichtige Inserate, Boost-Pakete und Händler-Abos nutzen wir Stripe Payments Europe Ltd. Zahlungsdaten werden ausschliesslich von Stripe verarbeitet. Wir speichern keine Karten- oder Bank-Daten. Datenschutzhinweise: stripe.com/privacy." },
        { h: "6. E-Mail-Versand — Resend", b: "Transaktionale E-Mails (Registrierung, Passwort-Reset, Saved-Search-Alerts, E-Mail-Wechsel-Bestätigung) versenden wir über Resend (USA, Standardvertragsklauseln). Es werden nur die zur Zustellung notwendigen Daten übermittelt." },
        { h: "7. Browser-Push-Benachrichtigungen", b: "Wenn du Push aktivierst, speichern wir den vom Browser bereitgestellten Subscription-Endpoint plus zwei öffentliche kryptografische Schlüssel. Wir senden Push nur für Saved-Searches, die du explizit aktiviert hast. Du kannst Push jederzeit in deinem Konto wieder deaktivieren." },
        { h: "8. Cookies und LocalStorage", b: "Wir verwenden technisch notwendige Cookies für Login-Session, Sprach-Einstellung und Schutz vor Missbrauch. Optional: LocalStorage für Vergleichsliste, kürzlich angesehene Inserate, PWA-Install-Hinweise. Es werden KEINE Tracking- oder Werbe-Cookies von Drittanbietern gesetzt." },
        { h: "9. Speicherdauer", b: "Konto-Daten so lange dein Konto besteht. Nach Löschung werden Inserate, Nachrichten und gespeicherte Suchen unwiderruflich entfernt. Aufbewahrungspflichtige Daten (z.B. Rechnungen) gemäss Schweizer OR 10 Jahre." },
        { h: "10. Deine Rechte", b: "Du hast jederzeit das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung, Widerspruch und Datenportabilität. Beschwerden kannst du beim Eidg. Datenschutzbeauftragten (EDÖB) einreichen. Anfragen: datenschutz@carhive.ch." },
        { h: "11. Sicherheit", b: "Alle Verbindungen sind TLS-verschlüsselt. Passwörter werden mit bcrypt gehasht. Stripe-Webhooks sind signaturgeprüft. Backups erfolgen verschlüsselt." },
        { h: "12. Änderungen", b: "Wir behalten uns vor, diese Erklärung anzupassen. Die aktuelle Fassung findest du jederzeit auf dieser Seite." },
      ],
    },
    terms: {
      title: "Allgemeine Geschäftsbedingungen (AGB)",
      subtitle: "Nutzungsbedingungen für die CarHive-Plattform.",
      sections: [
        { h: "1. Geltungsbereich", b: "Diese AGB regeln die Nutzung der Plattform CarHive (Web und PWA) durch private und gewerbliche Nutzer in der Schweiz." },
        { h: "2. Vertragspartner", b: "Anbieter und Vertragspartner ist:\n\nArnaldo Flávio Gonçalves Tafolli\nCarHive\nZelglistrasse 11\n8953 Dietikon\nSchweiz\n\nMit der Registrierung kommt ein Nutzungsvertrag zustande. Du musst mindestens 18 Jahre alt sein." },
        { h: "3. Inserate", b: "Inserate müssen wahrheitsgemäss, vollständig und in deutscher, französischer, italienischer oder englischer Sprache verfasst sein. Verboten sind Fake-Inserate, Mehrfacheinträge desselben Fahrzeugs sowie Bilder, an denen du keine Rechte hast." },
        { h: "4. Kommunikation", b: "Die direkte Nachrichtenfunktion dient ausschliesslich der Anbahnung von Kaufverträgen. Belästigungen, Spam und Phishing-Versuche führen zur sofortigen Sperrung." },
        { h: "5. Kostenpflichtige Leistungen", b: "Bezahlte Pakete (Boost, Händler-Plan, Premium-Inserate) werden via Stripe abgewickelt. Mit dem Klick auf «Kaufen» bestätigst du eine kostenpflichtige Bestellung. Es gilt das gewählte Paket — kein Auto-Renewal beim Boost." },
        { h: "6. Widerrufsrecht", b: "Bei digitalen Leistungen, die unmittelbar nach Kauf aktiviert werden (Boost-Hervorhebung, Inserats-Credits), verzichtest du auf das gesetzliche Widerrufsrecht. Für Abos gilt eine Kündigungsfrist gemäss Paketbeschreibung." },
        { h: "7. Verantwortlichkeit", b: "CarHive ist ein neutraler Marktplatz. Der Kaufvertrag entsteht ausschliesslich zwischen den Nutzern. Wir prüfen Inserate stichprobenartig, übernehmen aber keine Haftung für Fahrzeuge, deren Zustand oder Verkäufer-Identität." },
        { h: "8. Verhaltensregeln", b: "Du verpflichtest dich, keine illegalen, beleidigenden oder irreführenden Inhalte zu veröffentlichen. Wiederholte Verstösse führen zu permanenter Sperrung ohne Rückerstattung." },
        { h: "9. Datenschutz", b: "Es gilt unsere Datenschutzerklärung. Du stimmst der dort beschriebenen Verarbeitung zu." },
        { h: "10. Änderungen", b: "Wir können diese AGB anpassen. Wesentliche Änderungen kündigen wir 30 Tage vorher per E-Mail an. Wenn du nicht widersprichst, gelten sie als akzeptiert." },
        { h: "11. Anwendbares Recht & Gerichtsstand", b: "Es gilt Schweizer Recht unter Ausschluss des UN-Kaufrechts. Gerichtsstand ist Zürich, soweit gesetzlich zulässig." },
      ],
    },
    contact: {
      title: "Kontakt",
      subtitle: "Wir sind für dich da.",
      copy: "Hast du Fragen zur Plattform, ein technisches Problem oder ein Anliegen zum Datenschutz? Schreib uns — wir melden uns innerhalb von 24 Stunden.",
      formName: "Name",
      formEmail: "E-Mail",
      formSubject: "Betreff",
      formMessage: "Nachricht",
      formSubmit: "Nachricht senden",
      formSending: "Wird gesendet …",
      formSuccess: "Vielen Dank — wir haben deine Nachricht erhalten.",
      formError: "Senden fehlgeschlagen. Bitte versuche es später erneut oder schreibe direkt an info@carhive.ch direkt",
      contactDirect: "Direktkontakt",
    },
  },
  fr: {
    legalNav: { imprint: "Mentions légales", privacy: "Confidentialité", terms: "CGV", contact: "Contact" },
    back: "Retour",
    updated: "Mise à jour : mai 2026",
    imprint: {
      title: "Mentions légales",
      subtitle: "Informations selon le droit suisse.",
      operator: "Exploitant",
      contact: "Contact",
      register: "Inscription",
      uid: "IDE",
      disclaimerTitle: "Avis de non-responsabilité",
      disclaimer: "Les contenus de ce site sont rédigés avec le plus grand soin. Nous ne pouvons cependant garantir l'exactitude, l'exhaustivité ou l'actualité des informations fournies. Les annonces sont publiées par des tiers ; CarHive n'en assume pas la responsabilité.",
      linksTitle: "Liens externes",
      links: "Malgré un contrôle minutieux, nous déclinons toute responsabilité concernant le contenu des liens externes. Les exploitants des sites liés sont seuls responsables de leur contenu.",
      copyrightTitle: "Droits d'auteur",
      copyright: "Tous les contenus et œuvres de ce site sont protégés par le droit d'auteur suisse. Toute reproduction, modification ou diffusion nécessite l'accord écrit préalable.",
    },
    privacy: {
      title: "Politique de confidentialité",
      subtitle: "Conforme RGPD et LPD suisse.",
      sections: [
        { h: "1. Responsable du traitement", b: "Le responsable au sens de la LPD et du RGPD est :\n\nArnaldo Flávio Gonçalves Tafolli\nCarHive\nZelglistrasse 11\n8953 Dietikon\nSuisse\n\nContact : datenschutz@carhive.ch." },
        { h: "2. Données collectées", b: "Données de compte (e-mail, nom, hash de mot de passe), contenu des annonces et messages, géolocalisation des annonces, adresse IP (tronquée), cookies, journaux techniques de sécurité." },
        { h: "3. Finalités", b: "Fourniture du marché, gestion du compte, communication entre acheteurs et vendeurs, sécurité, conservation légale, analyse anonymisée pour amélioration du service." },
        { h: "4. Bases légales", b: "Contrat (art. 6 §1 b RGPD) pour le compte ; intérêt légitime (f) pour la sécurité ; consentement (a) pour les push, les e-mails marketing et les cookies non essentiels." },
        { h: "5. Paiements — Stripe", b: "Pour les forfaits payants, nous utilisons Stripe Payments Europe Ltd. Les données de paiement sont traitées exclusivement par Stripe. Nous ne stockons aucune donnée bancaire. Voir : stripe.com/privacy." },
        { h: "6. Envoi d'e-mails — Resend", b: "Les e-mails transactionnels (inscription, réinitialisation, alertes de recherche, confirmation de changement d'e-mail) passent par Resend (USA, clauses contractuelles types)." },
        { h: "7. Notifications push", b: "Si tu actives les push, nous stockons l'endpoint et deux clés cryptographiques publiques fournies par ton navigateur. Tu peux désactiver les push à tout moment dans ton compte." },
        { h: "8. Cookies & LocalStorage", b: "Cookies techniques pour la session, la langue et la protection contre les abus. LocalStorage pour la liste de comparaison, les véhicules récemment consultés et le rappel d'installation PWA. AUCUN cookie de tracking tiers." },
        { h: "9. Durée de conservation", b: "Données de compte tant que le compte existe. Après suppression : annonces, messages et recherches supprimés définitivement. Documents légaux (factures) : 10 ans selon le CO suisse." },
        { h: "10. Tes droits", b: "Droits d'accès, rectification, suppression, limitation, opposition, portabilité. Plainte possible auprès du PFPDT. Contact : datenschutz@carhive.ch." },
        { h: "11. Sécurité", b: "Connexions chiffrées TLS. Mots de passe hashés (bcrypt). Webhooks Stripe vérifiés par signature. Sauvegardes chiffrées." },
        { h: "12. Modifications", b: "Nous nous réservons le droit de modifier cette politique. La version actuelle est toujours consultable sur cette page." },
      ],
    },
    terms: {
      title: "Conditions générales (CGV)",
      subtitle: "Conditions d'utilisation de la plateforme CarHive.",
      sections: [
        { h: "1. Champ d'application", b: "Ces CGV régissent l'utilisation de la plateforme CarHive (Web et PWA) par les utilisateurs privés et professionnels en Suisse." },
        { h: "2. Cocontractant", b: "Le fournisseur et cocontractant est :\n\nArnaldo Flávio Gonçalves Tafolli\nCarHive\nZelglistrasse 11\n8953 Dietikon\nSuisse\n\nL'inscription forme un contrat d'utilisation. Tu dois avoir au moins 18 ans." },
        { h: "3. Annonces", b: "Les annonces doivent être véridiques, complètes et rédigées en allemand, français, italien ou anglais. Sont interdits : faux comptes, multi-annonces du même véhicule, photos sans droits." },
        { h: "4. Communication", b: "La messagerie sert uniquement à la préparation d'achats. Harcèlement, spam et phishing entraînent la suspension immédiate." },
        { h: "5. Services payants", b: "Les forfaits payants (Boost, Dealer, Premium) sont traités via Stripe. En cliquant « Acheter » tu confirmes une commande payante. Pas de renouvellement automatique pour le Boost." },
        { h: "6. Droit de rétractation", b: "Pour les prestations numériques activées immédiatement (boost, crédits d'annonce), tu renonces au droit de rétractation légal. Pour les abonnements, le délai s'applique selon la description du forfait." },
        { h: "7. Responsabilité", b: "CarHive est un marché neutre. Le contrat de vente naît uniquement entre les utilisateurs. Nous contrôlons par échantillonnage mais déclinons toute responsabilité quant à l'état des véhicules ou l'identité des vendeurs." },
        { h: "8. Règles de conduite", b: "Aucun contenu illégal, injurieux ou trompeur. Les récidives entraînent une suspension permanente sans remboursement." },
        { h: "9. Protection des données", b: "Notre politique de confidentialité s'applique. Tu acceptes le traitement qui y est décrit." },
        { h: "10. Modifications", b: "Nous pouvons adapter ces CGV. Les modifications importantes sont annoncées 30 jours à l'avance par e-mail. Sans opposition, elles sont considérées comme acceptées." },
        { h: "11. Droit applicable", b: "Droit suisse à l'exclusion de la CVIM. For : Zurich, dans la mesure légalement admise." },
      ],
    },
    contact: {
      title: "Contact",
      subtitle: "Nous sommes là pour toi.",
      copy: "Une question, un problème technique ou une demande de confidentialité ? Écris-nous — nous répondons sous 24 heures.",
      formName: "Nom",
      formEmail: "E-mail",
      formSubject: "Sujet",
      formMessage: "Message",
      formSubmit: "Envoyer le message",
      formSending: "Envoi en cours…",
      formSuccess: "Merci — nous avons bien reçu ton message.",
      formError: "Échec de l'envoi. Réessaie plus tard ou écris à info@carhive.ch direkt",
      contactDirect: "Contact direct",
    },
  },
  it: {
    legalNav: { imprint: "Note legali", privacy: "Privacy", terms: "Termini", contact: "Contatto" },
    back: "Indietro",
    updated: "Aggiornamento: maggio 2026",
    imprint: {
      title: "Note legali",
      subtitle: "Informazioni secondo il diritto svizzero.",
      operator: "Operatore",
      contact: "Contatto",
      register: "Registro",
      uid: "IDI",
      disclaimerTitle: "Esclusione di responsabilità",
      disclaimer: "I contenuti di questo sito sono redatti con la massima cura. Tuttavia non garantiamo l'esattezza, la completezza o l'attualità delle informazioni. Gli annunci sono pubblicati da terzi; CarHive non è responsabile del loro contenuto.",
      linksTitle: "Link esterni",
      links: "Nonostante il controllo attento, decliniamo ogni responsabilità per i contenuti di siti esterni. Gli operatori dei siti collegati sono unici responsabili.",
      copyrightTitle: "Diritto d'autore",
      copyright: "Tutti i contenuti di questo sito sono protetti dal diritto d'autore svizzero. Riproduzione, modifica o diffusione richiedono un previo consenso scritto.",
    },
    privacy: {
      title: "Informativa sulla privacy",
      subtitle: "Conforme GDPR e LPD svizzera.",
      sections: [
        { h: "1. Titolare del trattamento", b: "Il titolare ai sensi della LPD e del GDPR è:\n\nArnaldo Flávio Gonçalves Tafolli\nCarHive\nZelglistrasse 11\n8953 Dietikon\nSvizzera\n\nContatto: datenschutz@carhive.ch." },
        { h: "2. Dati raccolti", b: "Dati account (e-mail, nome, hash password), contenuto di annunci e messaggi, posizione degli annunci, indirizzo IP (troncato), cookie, log tecnici di sicurezza." },
        { h: "3. Finalità", b: "Fornitura del mercato, gestione del conto, comunicazione tra acquirente e venditore, sicurezza, conservazione legale, analisi anonimizzate." },
        { h: "4. Basi giuridiche", b: "Contratto (art. 6 §1 b GDPR) per account e annunci; interesse legittimo (f) per sicurezza; consenso (a) per push, e-mail marketing e cookie non essenziali." },
        { h: "5. Pagamenti — Stripe", b: "Per i pacchetti a pagamento utilizziamo Stripe Payments Europe Ltd. I dati di pagamento sono trattati esclusivamente da Stripe. Non memorizziamo dati di carte. Vedi: stripe.com/privacy." },
        { h: "6. Invio e-mail — Resend", b: "Le e-mail transazionali passano da Resend (USA, clausole contrattuali tipo)." },
        { h: "7. Notifiche push", b: "Se attivi le push, conserviamo l'endpoint e due chiavi crittografiche pubbliche fornite dal browser. Puoi disattivarle in qualsiasi momento dal tuo account." },
        { h: "8. Cookie & LocalStorage", b: "Cookie tecnici per sessione, lingua e protezione anti-abuso. LocalStorage per lista confronto, veicoli visti di recente, prompt PWA. NESSUN cookie di tracking di terzi." },
        { h: "9. Conservazione", b: "Dati del conto finché esiste il conto. Dopo cancellazione: annunci, messaggi e ricerche eliminati definitivamente. Documenti contabili: 10 anni secondo il CO svizzero." },
        { h: "10. I tuoi diritti", b: "Diritti di accesso, rettifica, cancellazione, limitazione, opposizione, portabilità. Reclamo presso l'IFPDT possibile. Contatto: datenschutz@carhive.ch." },
        { h: "11. Sicurezza", b: "Connessioni TLS, password hashate (bcrypt), webhook Stripe firmati, backup cifrati." },
        { h: "12. Modifiche", b: "Ci riserviamo di modificare questa informativa. La versione attuale è sempre disponibile su questa pagina." },
      ],
    },
    terms: {
      title: "Termini di servizio",
      subtitle: "Condizioni d'uso della piattaforma CarHive.",
      sections: [
        { h: "1. Ambito", b: "Questi termini regolano l'uso di CarHive (Web e PWA) da parte di utenti privati e professionali in Svizzera." },
        { h: "2. Parte contraente", b: "Il fornitore e parte contraente è:\n\nArnaldo Flávio Gonçalves Tafolli\nCarHive\nZelglistrasse 11\n8953 Dietikon\nSvizzera\n\nLa registrazione costituisce un contratto. Devi avere almeno 18 anni." },
        { h: "3. Annunci", b: "Gli annunci devono essere veritieri, completi e in tedesco, francese, italiano o inglese. Vietati: account falsi, duplicati dello stesso veicolo, foto senza diritti." },
        { h: "4. Comunicazione", b: "La messaggistica serve solo a preparare l'acquisto. Molestie, spam e phishing portano alla sospensione immediata." },
        { h: "5. Servizi a pagamento", b: "Pacchetti a pagamento (Boost, Dealer, Premium) gestiti via Stripe. Cliccando «Acquista» confermi un ordine a pagamento. Nessun rinnovo automatico per il Boost." },
        { h: "6. Diritto di recesso", b: "Per servizi digitali attivati immediatamente (boost, crediti annuncio), rinunci al diritto di recesso legale. Per gli abbonamenti vale la disdetta indicata nel pacchetto." },
        { h: "7. Responsabilità", b: "CarHive è un marketplace neutrale. Il contratto di acquisto nasce solo tra utenti. Verifichiamo a campione ma non assumiamo responsabilità per le condizioni dei veicoli o l'identità dei venditori." },
        { h: "8. Regole di comportamento", b: "Vietati contenuti illegali, offensivi o ingannevoli. Recidive: sospensione permanente senza rimborso." },
        { h: "9. Privacy", b: "Si applica la nostra informativa sulla privacy." },
        { h: "10. Modifiche", b: "Possiamo modificare i termini. Modifiche sostanziali notificate via e-mail 30 giorni prima. Senza opposizione, sono accettate." },
        { h: "11. Diritto applicabile", b: "Diritto svizzero, esclusa la CISG. Foro: Zurigo, nei limiti consentiti dalla legge." },
      ],
    },
    contact: {
      title: "Contatto",
      subtitle: "Siamo qui per te.",
      copy: "Domande sulla piattaforma, problemi tecnici o richieste sulla privacy? Scrivici — rispondiamo entro 24 ore.",
      formName: "Nome",
      formEmail: "E-mail",
      formSubject: "Oggetto",
      formMessage: "Messaggio",
      formSubmit: "Invia messaggio",
      formSending: "Invio in corso…",
      formSuccess: "Grazie — abbiamo ricevuto il tuo messaggio.",
      formError: "Invio fallito. Riprova più tardi o scrivi a info@carhive.ch direkt",
      contactDirect: "Contatto diretto",
    },
  },
  en: {
    legalNav: { imprint: "Imprint", privacy: "Privacy", terms: "Terms", contact: "Contact" },
    back: "Back",
    updated: "Last updated: May 2026",
    imprint: {
      title: "Imprint",
      subtitle: "Information according to Swiss law.",
      operator: "Operator",
      contact: "Contact",
      register: "Registration",
      uid: "VAT ID",
      disclaimerTitle: "Disclaimer",
      disclaimer: "The content of this website is compiled with the greatest care. However, we cannot guarantee its accuracy, completeness or timeliness. Listings are published by third-party users; CarHive is not responsible for their content.",
      linksTitle: "External links",
      links: "Despite careful review, we do not accept liability for the content of external links. The operators of linked sites are solely responsible for their content.",
      copyrightTitle: "Copyright",
      copyright: "All content and works on this website are subject to Swiss copyright law. Reproduction, modification or distribution requires prior written consent.",
    },
    privacy: {
      title: "Privacy Policy",
      subtitle: "GDPR and Swiss DPA compliant.",
      sections: [
        { h: "1. Controller", b: "The controller within the meaning of Swiss DPA and the GDPR is:\n\nArnaldo Flávio Gonçalves Tafolli\nCarHive\nZelglistrasse 11\n8953 Dietikon\nSwitzerland\n\nContact: datenschutz@carhive.ch." },
        { h: "2. Data we collect", b: "Account data (email, name, password hash), listing and message content, listing location, IP address (truncated), cookies, technical security logs." },
        { h: "3. Purposes", b: "Marketplace operation, account management, buyer-seller communication, abuse prevention, legal retention, anonymised analytics for service improvement." },
        { h: "4. Legal bases", b: "Contract (Art. 6(1)(b) GDPR) for account and listings; legitimate interest (f) for security; consent (a) for push notifications, marketing emails and non-essential cookies." },
        { h: "5. Payments — Stripe", b: "For paid plans we use Stripe Payments Europe Ltd. Payment data is processed exclusively by Stripe. We do not store any card or bank data. See: stripe.com/privacy." },
        { h: "6. Email — Resend", b: "Transactional emails (registration, password reset, saved-search alerts, email-change confirmation) are sent via Resend (USA, Standard Contractual Clauses)." },
        { h: "7. Browser push notifications", b: "If you enable push, we store the subscription endpoint plus two public cryptographic keys provided by your browser. You can disable push anytime in your account." },
        { h: "8. Cookies & LocalStorage", b: "Technical cookies for session, language and abuse protection. LocalStorage for compare list, recently viewed listings and PWA install hint. NO third-party tracking cookies." },
        { h: "9. Retention", b: "Account data while the account exists. After deletion: listings, messages and saved searches are permanently removed. Records subject to retention (e.g. invoices): 10 years per Swiss CO." },
        { h: "10. Your rights", b: "Access, rectification, deletion, restriction, objection and portability. Complaints can be filed with the Swiss FDPIC. Requests: datenschutz@carhive.ch." },
        { h: "11. Security", b: "All connections TLS-encrypted. Passwords hashed with bcrypt. Stripe webhooks signature-verified. Encrypted backups." },
        { h: "12. Changes", b: "We may update this policy. The current version is always available on this page." },
      ],
    },
    terms: {
      title: "Terms of Service",
      subtitle: "Conditions of use for the CarHive platform.",
      sections: [
        { h: "1. Scope", b: "These terms govern the use of CarHive (Web and PWA) by private and commercial users in Switzerland." },
        { h: "2. Contracting party", b: "The provider and contracting party is:\n\nArnaldo Flávio Gonçalves Tafolli\nCarHive\nZelglistrasse 11\n8953 Dietikon\nSwitzerland\n\nRegistration constitutes a user agreement. You must be at least 18 years old." },
        { h: "3. Listings", b: "Listings must be truthful, complete and written in German, French, Italian or English. Forbidden: fake accounts, duplicate listings for the same vehicle, photos you don't own rights to." },
        { h: "4. Communication", b: "Direct messaging is for sale preparation only. Harassment, spam and phishing lead to immediate suspension." },
        { h: "5. Paid services", b: "Paid plans (Boost, Dealer, Premium) are handled via Stripe. Clicking 'Buy' confirms a paid order. Boost has no auto-renewal." },
        { h: "6. Right of withdrawal", b: "For digital services activated immediately (boost, listing credits) you waive the statutory withdrawal right. For subscriptions, the notice period in the plan description applies." },
        { h: "7. Liability", b: "CarHive is a neutral marketplace. Sales contracts arise only between users. We check listings on a sample basis but accept no liability for vehicle condition or seller identity." },
        { h: "8. Conduct", b: "No illegal, offensive or misleading content. Repeated violations: permanent suspension without refund." },
        { h: "9. Data protection", b: "Our Privacy Policy applies." },
        { h: "10. Changes", b: "We may amend these terms. Material changes are announced 30 days in advance by email. Without objection, they are deemed accepted." },
        { h: "11. Governing law", b: "Swiss law applies, excluding the UN CISG. Place of jurisdiction is Zurich, where legally permitted." },
      ],
    },
    contact: {
      title: "Contact",
      subtitle: "We're here for you.",
      copy: "Have a question about the platform, a technical issue, or a privacy request? Write to us — we reply within 24 hours.",
      formName: "Name",
      formEmail: "Email",
      formSubject: "Subject",
      formMessage: "Message",
      formSubmit: "Send message",
      formSending: "Sending…",
      formSuccess: "Thanks — we've received your message.",
      formError: "Send failed. Please try again later or email info@carhive.ch directly.",
      contactDirect: "Direct contact",
    },
  },
};

function useLocaleContent() {
  const { lang } = useLang();
  return CONTENT[lang] || CONTENT.de;
}

// ---------------------------------------------------------------------------
// Shared layout
// ---------------------------------------------------------------------------
function LegalShell({ icon, title, subtitle, children, testId }) {
  const { lang } = useLang();
  const c = useLocaleContent();
  useEffect(() => {
    document.title = `${title} · CarHive`;
    return () => { document.title = "CarHive"; };
  }, [title]);
  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8" data-testid={testId}>
      <Link
        to={buildPath(lang, "/")}
        className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-white"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> {c.back}
      </Link>
      <div className="mt-6 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-600/15 text-red-400">{icon}</span>
        <div className="min-w-0">
          <h1 className="font-display text-3xl font-bold text-white sm:text-4xl">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-zinc-400">{subtitle}</p>}
        </div>
      </div>
      <p className="mt-4 text-[11px] uppercase tracking-[0.18em] text-zinc-600">{c.updated}</p>
      <div className="mt-8 space-y-6 text-sm leading-relaxed text-zinc-300">{children}</div>
    </div>
  );
}

function LegalSection({ h, b }) {
  return (
    <section>
      <h2 className="font-display text-base font-semibold text-white sm:text-lg">{h}</h2>
      <p className="mt-2 whitespace-pre-line text-zinc-400">{b}</p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// PAGES
// ---------------------------------------------------------------------------

/* Phase 71 — Premium Impressum with conditional rendering. Address, phone,
 * UID and Handelsregister appear only when their `has*` flag is true in
 * /config/company.js — no placeholders are ever rendered. */
export function ImprintPage() {
  const { lang } = useLang();
  const c = useLocaleContent();
  const im = c.imprint;
  const tIm = TRUST_BLOCK_I18N[lang] || TRUST_BLOCK_I18N.de;
  return (
    <LegalShell icon={<FileText className="h-5 w-5" />} title={im.title} subtitle={im.subtitle} testId="page-imprint">
      {/* Operator + Contact card */}
      <div className="grid gap-6 rounded-2xl border border-zinc-800 bg-gradient-to-br from-zinc-950 via-black to-zinc-950 p-5 sm:grid-cols-2 sm:p-7" data-testid="imprint-operator-card">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">{im.operator}</p>
          <p className="mt-2 font-display text-lg font-semibold text-white" data-testid="imprint-operator-name">
            {COMPANY.operator.fullName}
          </p>
          <p className="text-sm text-zinc-500">{COMPANY.name}</p>
          {COMPANY.hasAddress && (
            <div className="mt-2 text-zinc-400" data-testid="imprint-operator-address">
              <p>{COMPANY.operator.street}</p>
              <p>{COMPANY.operator.zip} {COMPANY.operator.city}</p>
              <p>{COMPANY.operator.country}</p>
            </div>
          )}
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">{im.contact}</p>
          <a
            href={`mailto:${COMPANY.emails.general}`}
            className="mt-2 flex items-center gap-2 text-zinc-200 hover:text-white"
            data-testid="imprint-email-general"
          >
            <Mail className="h-4 w-4 text-red-400" />
            <span className="break-all">{COMPANY.emails.general}</span>
          </a>
          {COMPANY.hasPhone && COMPANY.phone && (
            <a
              href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
              className="mt-1 flex items-center gap-2 text-zinc-200 hover:text-white"
              data-testid="imprint-phone"
            >
              <Phone className="h-4 w-4 text-red-400" />
              <span>{COMPANY.phone}</span>
            </a>
          )}
        </div>

        {COMPANY.hasCommercialRegister && (
          <div className="sm:col-span-2 border-t border-zinc-800 pt-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">{im.register}</p>
            <p className="mt-2 text-zinc-400">{COMPANY.commercialRegister}</p>
            {COMPANY.uid && <p className="text-zinc-400">{im.uid}: {COMPANY.uid}</p>}
          </div>
        )}
      </div>

      {/* Phase 71 — Support / response-time / additional contacts block */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-950 p-5 sm:p-7" data-testid="imprint-support-card">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-emerald-400" />
          <p className="font-display text-base font-semibold text-white sm:text-lg">{tIm.supportHeading}</p>
        </div>
        <p className="mt-2 text-sm text-zinc-400">{tIm.supportLead}</p>
        <p className="mt-3 inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs text-emerald-200">
          <Clock className="h-3 w-3" /> {RESPONSE_TIME_COPY[lang] || RESPONSE_TIME_COPY.de}
        </p>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <ContactRow icon={<Mail className="h-3.5 w-3.5" />} label={tIm.supportEmail} value={COMPANY.emails.support} testId="imprint-email-support" />
          <ContactRow icon={<ShieldCheck className="h-3.5 w-3.5" />} label={tIm.privacyEmail} value={COMPANY.emails.privacy} testId="imprint-email-privacy" />
          <ContactRow icon={<Scale className="h-3.5 w-3.5" />} label={tIm.legalEmail} value={COMPANY.emails.legal} testId="imprint-email-legal" />
          {COMPANY.hasWhatsapp && COMPANY.whatsappUrl && (
            <a
              href={COMPANY.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="imprint-whatsapp"
              className="inline-flex items-center justify-between gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-3 text-sm text-emerald-200 hover:bg-emerald-500/15"
            >
              <span className="flex items-center gap-2">
                <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
              </span>
              <span className="text-xs opacity-70">→</span>
            </a>
          )}
        </div>
      </div>

      <LegalSection h={im.disclaimerTitle} b={im.disclaimer} />
      <LegalSection h={im.linksTitle} b={im.links} />
      <LegalSection h={im.copyrightTitle} b={im.copyright} />
    </LegalShell>
  );
}

/* Small labelled mailto row used inside the Imprint support card. */
function ContactRow({ icon, label, value, testId }) {
  return (
    <a
      href={`mailto:${value}`}
      data-testid={testId}
      className="flex items-center justify-between gap-3 rounded-lg border border-zinc-800 bg-black/40 px-3 py-2.5 text-sm text-zinc-200 hover:border-zinc-700 hover:text-white"
    >
      <span className="flex min-w-0 items-center gap-2">
        <span className="text-red-400">{icon}</span>
        <span className="flex flex-col leading-tight">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</span>
          <span className="break-all">{value}</span>
        </span>
      </span>
    </a>
  );
}

const TRUST_BLOCK_I18N = {
  de: { supportHeading: "Support & Kontakt", supportLead: "Frag uns alles rund um die Plattform, dein Konto oder rechtliche Themen. Wir antworten schnell und persönlich.", supportEmail: "Support", privacyEmail: "Datenschutz", legalEmail: "Recht" },
  fr: { supportHeading: "Support & Contact", supportLead: "Pose-nous toutes tes questions sur la plateforme, ton compte ou les aspects juridiques. Nous répondons rapidement et personnellement.", supportEmail: "Support", privacyEmail: "Confidentialité", legalEmail: "Juridique" },
  it: { supportHeading: "Supporto & Contatto", supportLead: "Chiedici tutto sulla piattaforma, sul tuo conto o su aspetti legali. Rispondiamo rapidamente e in modo personale.", supportEmail: "Supporto", privacyEmail: "Privacy", legalEmail: "Legale" },
  en: { supportHeading: "Support & Contact", supportLead: "Ask us anything about the platform, your account, or legal topics. We reply quickly and personally.", supportEmail: "Support", privacyEmail: "Privacy", legalEmail: "Legal" },
};

export function PrivacyPage() {
  const c = useLocaleContent();
  return (
    <LegalShell icon={<ShieldCheck className="h-5 w-5" />} title={c.privacy.title} subtitle={c.privacy.subtitle} testId="page-privacy">
      {c.privacy.sections.map((s, i) => <LegalSection key={i} {...s} />)}
    </LegalShell>
  );
}

export function TermsPage() {
  const c = useLocaleContent();
  return (
    <LegalShell icon={<Scale className="h-5 w-5" />} title={c.terms.title} subtitle={c.terms.subtitle} testId="page-terms">
      {c.terms.sections.map((s, i) => <LegalSection key={i} {...s} />)}
    </LegalShell>
  );
}
